if (!customElements.get('quantity-shortcuts')) {
  customElements.define(
    'quantity-shortcuts',
    class QuantityShortcuts extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.input = this.productInfo?.querySelector('.quantity__input');
        if (!this.input) return;

        this.addEventListener('click', this.onClick);
        this.input.addEventListener('input', this.sync);
        this.input.addEventListener('change', this.sync);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.sync);
          this.optionSelectionUnsubscriber = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.onOptionSelection
          );
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }
        this.sync();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.input?.removeEventListener('input', this.sync);
        this.input?.removeEventListener('change', this.sync);
        this.quantityUpdateUnsubscriber?.();
        this.optionSelectionUnsubscriber?.();
        this.variantChangeUnsubscriber?.();
      }

      onOptionSelection = (event) => {
        if (!this.productInfo?.contains(event?.data?.event?.target)) return;
        this.quantityToRestore = this.input.value;
      };

      onVariantChange = (event) => {
        if (event?.data?.sectionId !== this.productInfo?.dataset.section) return;

        const incomingShortcuts = event.data.html?.querySelector('[data-volume-shortcuts]');
        const incomingVariantIsEligible = incomingShortcuts && !incomingShortcuts.hasAttribute('hidden');

        if (incomingVariantIsEligible && this.quantityToRestore !== undefined) {
          this.restoreQuantity(this.quantityToRestore);
        }

        this.quantityToRestore = undefined;
        this.sync();
      };

      restoreQuantity(requestedQuantity) {
        const ruleMinimum = Number(this.input.dataset.min || 1);
        const step = Number(this.input.step || 1);
        const cartQuantity = Number(this.input.dataset.cartQuantity || 0);
        const configuredMaximum = this.input.dataset.max ? Number(this.input.dataset.max) : null;
        const minimum = cartQuantity >= ruleMinimum ? Math.min(ruleMinimum, step) : ruleMinimum;
        const maximum = configuredMaximum === null ? null : Math.max(configuredMaximum - cartQuantity, minimum);

        let quantity = Number(requestedQuantity);
        if (!Number.isFinite(quantity)) quantity = minimum;
        quantity = Math.max(quantity, minimum);
        if (maximum !== null) quantity = Math.min(quantity, maximum);

        if (step > 1 && quantity > ruleMinimum) {
          quantity = ruleMinimum + Math.floor((quantity - ruleMinimum) / step) * step;
          quantity = Math.max(quantity, minimum);
        }

        this.input.min = minimum;
        if (maximum === null) {
          this.input.removeAttribute('max');
        } else {
          this.input.max = maximum;
        }
        this.input.value = quantity;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onClick = (event) => {
        const button = event.target.closest('button[data-quantity]');
        if (!button || !this.input) return;

        const requested = Number(button.dataset.quantity);
        const minimum = Number(this.input.min || 1);
        const maximum = this.input.max ? Number(this.input.max) : requested;
        this.input.value = Math.max(minimum, Math.min(requested, maximum));
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      sync = () => {
        const current = Number(this.input?.value || 0);
        this.querySelectorAll('button[data-quantity]').forEach((button) => {
          button.setAttribute('aria-pressed', String(Number(button.dataset.quantity) === current));
        });
      };
    }
  );
}

if (!customElements.get('dop-order-summary')) {
  customElements.define(
    'dop-order-summary',
    class DOPOrderSummary extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.quantityInput = this.productInfo?.querySelector('.quantity__input');
        this.submitButton = this.productInfo?.querySelector('.product-form__submit');
        this.config = this.readConfig();

        if (!this.productInfo || !this.quantityInput || !this.config) return;

        this.quantityInput.addEventListener('input', this.onQuantityChange);
        this.quantityInput.addEventListener('change', this.onQuantityChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
          this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantityChange);
        }

        this.render();
      }

      disconnectedCallback() {
        this.quantityInput?.removeEventListener('input', this.onQuantityChange);
        this.quantityInput?.removeEventListener('change', this.onQuantityChange);
        this.variantChangeUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
      }

      readConfig() {
        const configElement = this.querySelector('[data-order-summary-config]');
        if (!configElement) return null;

        try {
          return JSON.parse(configElement.textContent);
        } catch (error) {
          console.error('Unable to initialize the product order summary.', error);
          return null;
        }
      }

      onQuantityChange = () => {
        this.render();
      };

      onVariantChange = (event) => {
        if (event?.data?.sectionId && event.data.sectionId !== this.productInfo?.dataset.section) return;
        this.render();
      };

      getQuantity() {
        const minimum = Number(this.quantityInput?.min || this.quantityInput?.dataset.min || 1);
        const quantity = Number(this.quantityInput?.value);
        return Number.isFinite(quantity) && quantity > 0 ? quantity : minimum;
      }

      formatCurrency(cents) {
        const locale = this.config.locale || document.documentElement.lang || 'en';
        const currency = this.config.currency || 'USD';

        try {
          return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
        } catch (_error) {
          return `$${(cents / 100).toFixed(2)}`;
        }
      }

      render() {
        if (!this.config || !this.quantityInput) return;

        const quantity = this.getQuantity();
        const unit = quantity === 1 ? this.config.singularUnit : this.config.pluralUnit;
        const discountThreshold = Number(this.config.discountThreshold || 150);
        const bulkThreshold = Number(this.config.bulkThreshold || 401);
        const isEligible = this.config.eligible === true;
        const discountIsVerified = isEligible && this.config.discountVerified === true;
        const isDiscountQuantity = discountIsVerified && quantity >= discountThreshold;
        const isBulkQuantity = isEligible && quantity >= bulkThreshold;

        this.querySelector('[data-order-quantity]').textContent = quantity;
        this.querySelector('[data-order-unit]').textContent = unit;
        this.querySelector('[data-order-subtotal]').textContent = this.formatCurrency(quantity * Number(this.config.price));
        this.querySelector('[data-order-subtotal-label]').textContent = isDiscountQuantity
          ? this.config.subtotalBeforeDiscountLabel
          : this.config.subtotalLabel;

        const messages = [];
        if (discountIsVerified) {
          messages.push(
            quantity >= discountThreshold ? this.config.discountMessage : this.config.belowThresholdMessage
          );
        }
        if (isBulkQuantity) messages.push(this.config.bulkMessage);
        this.renderGuidance(messages.filter(Boolean));

        const bulkLink = this.querySelector('[data-order-bulk-link]');
        if (bulkLink) bulkLink.hidden = !isBulkQuantity;

        const shortcuts = this.productInfo.querySelector('[data-volume-shortcuts]');
        if (shortcuts) shortcuts.hidden = !isEligible;

        this.updateSubmitButton(quantity, unit);
      }

      renderGuidance(messages) {
        const guidance = this.querySelector('[data-order-guidance]');
        if (!guidance) return;

        guidance.replaceChildren(
          ...messages.map((message) => {
            const paragraph = document.createElement('p');
            paragraph.textContent = message;
            return paragraph;
          })
        );
        guidance.hidden = messages.length === 0;
      }

      updateSubmitButton(quantity, unit) {
        if (!this.submitButton || this.config.available !== true || this.submitButton.disabled) return;
        const label = this.submitButton.querySelector('span');
        if (!label) return;

        label.textContent = `${this.config.addButtonPrefix} ${quantity} ${unit} ${this.config.addButtonSuffix}`;
      }
    }
  );
}
